import { AllMenusQuery, useFetchAllMenus } from '@@partner:graphql/query/menu/allMenus.gql'
import useFetchMorePagination from '@@partner:helpers/hooks/useFetchMorePagination'
import { usePartner } from '@@partner:helpers/hooks/auth'
import { renderLazyPickerMenu } from '@components/common/pickers'
import React, { useCallback } from 'react'
import { Button, SelectPicker } from 'rsuite'
import { MainPageBlockForm } from '../form/MainPageConfigFormState'
import EntityLink from './EntityLink'
import { ActiveEditorState } from '@@partner:containers/marketing/mainPageConfig/MainPageConfigFormPage'
import EditIcon from '@rsuite/icons/Edit'

type MenuBlockProps = {
  block: MainPageBlockForm
  onChange: (block: MainPageBlockForm) => void
  onOpenEditor?: (editor: ActiveEditorState) => void
}

export default function MenuBlock ({
  block,
  onChange,
  onOpenEditor
}: MenuBlockProps) {
  const { brandSlug } = usePartner()
  const [ nameFilter, setNameFilter ] = React.useState<string>('')

  const { data, loading, error, fetchMore } = useFetchAllMenus({
    filter: {
      fields: [ 'name' ],
      op: nameFilter.length > 0 ? 'starts_with' : 'unwrap',
      value: [ nameFilter ]
    },
    pager: { 
      limit: 20
    },
    sort: {
      fieldName: 'created_at',
      direction: -1
    }
  })

  const updaterFunction = useCallback((cached: AllMenusQuery, fetched: AllMenusQuery) => {
    const prev = cached?.allMenus?.edges ?? []
    const curr = fetched?.allMenus?.edges ?? []

    return {
      hasMore: curr.length > 0,
      result: {
        allMenus: {
          ...fetched.allMenus,
          edges: [ ...prev, ...curr ]
        }
      }
    }
  }, [])

  const { hasMore, fetchNextPage } = useFetchMorePagination({
    loading,
    page: data?.allMenus.pageInfo.page ?? 1,
    pageVariable: 'pager',
    usePager: true,
    fetchMore,
    updaterFunction,
  })

  const menus = data?.allMenus.edges.map((menu) => ({
    label: `${menu.name} (${menu.type})`,
    value: menu.code,
  })) ?? []

  // Добавляем выбранное меню в список если его нет в загруженных данных
  const selectedMenuInList = menus.find(m => m.value === block.payload?.code)
  const menusWithSelected = selectedMenuInList 
    ? menus 
    : block.payload?.code && block.payload?.name
      ? [ { label: block.payload.name, value: block.payload.code }, ...menus ]
      : menus

  const onItemsRendered = (props: any) => {
    if (props.visibleStopIndex >= menus.length - 1 && hasMore) {
      fetchNextPage()
    }
  }

  const selectedMenu = data?.allMenus.edges.find(m => m.code === block.payload?.code)

  const handleOpenEditor = useCallback(() => {
    const menuId = block.payload?.id ?? selectedMenu?.id
    if (menuId && block.payload?.code && onOpenEditor) {
      onOpenEditor({
        type: 'menu',
        menuId,
        menuCode: block.payload.code,
        menuName: selectedMenu?.name ?? block.payload?.name
      })
    }
  }, [ block.payload?.id, block.payload?.code, block.payload?.name, selectedMenu, onOpenEditor ])

  return (
    <div>
      <h4>Выберите меню:</h4>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <SelectPicker
          data={menusWithSelected}
          value={block.payload?.code}
          loading={loading && !data}
          onSelect={(value) => {
            if (onChange && value) {
              const menu = data?.allMenus.edges.find(m => m.code === value)
              onChange({
                ...block,
                payload: {
                  code: value,
                  id: menu?.id,
                  name: menu ? `${menu.name} (${menu.type})` : undefined,
                }
              })
            }
          }}
          onSearch={setNameFilter}
          renderMenu={renderLazyPickerMenu(loading, data)}
          placeholder="Выберите меню"
          listProps={{
            onItemsRendered
          }}
          style={{ flex: 1 }}
        />
        <EntityLink
          entityId={block.payload?.id ?? selectedMenu?.id}
          entityType="menu"
          brandSlug={brandSlug}
          tooltip="Перейти к меню"
        />
      </div>
      {(block.payload?.id ?? selectedMenu?.id) && (
        <Button
          appearance="link"
          size="sm"
          startIcon={<EditIcon />}
          onClick={handleOpenEditor}
          style={{ marginTop: 8 }}
        >
          Редактировать пункты меню
        </Button>
      )}
    </div>
  )
}
